import Callout from 'src/components/InformationCallout/Callout';
import { useEffect, useState } from 'react';
import { ISuite } from 'src/components/SharedTypes';
import {
  filterUpdatedSelectedSuiteIds,
  filterValidSelectedSuiteIds,
} from 'src/components/BulkOperations/helpers';
import TextEditor from '../../../Editor/Editor';
import BulkKeyPickupsSuiteTable from 'src/components/BulkOperations/KeyPickups/BulkKeyPickupsSuiteTable/BulkKeyPickupsSuiteTable';
import {
  BulkKeyPickupPayloadFromUI,
  KeyValPairNoID,
  KeyValuePair,
} from 'src/components/SuitesTable/CustomHooks/useKeyPickups';
import KeyValuePairs from 'src/components/KeyValuePairs/KeyValuePairs';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import './BulkAddKeyPickups.scss';

interface BulkAddKeyPickupsProps {
  userId: string;
  selectedSuiteIds: string[];
  onSave?: (params: any) => void;
  suites: ISuite[];
  propertyId: number;
  isSubmitting: boolean
}

const BulkAddKeyPickups = ({
  userId,
  selectedSuiteIds: initialSelectedSuiteIds,
  onSave,
  suites,
  propertyId,
  isSubmitting
}: BulkAddKeyPickupsProps) => {
  const [instructions, setInstructions] = useState<string>('');
  const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([]);
  const [selectedSuiteIds, setSelectedSuiteIds] = useState<string[]>(
    initialSelectedSuiteIds
  );
  const [hasKeyValueValidationErrors, setHasKeyValueValidationErrors] =
    useState<boolean>(false);

  const totalSuites: number = suites.length;
  const eligibleCount: number = suites.filter(
    (suite: ISuite) => suite.isKeyHandOff === false
  ).length;

  useEffect(() => {
    if (initialSelectedSuiteIds.length === 0) {
      return;
    }

    const validSelectedSuiteIds: string[] = filterValidSelectedSuiteIds(
      suites,
      initialSelectedSuiteIds
    );

    setSelectedSuiteIds(validSelectedSuiteIds);
  }, [initialSelectedSuiteIds, suites]);

  const handleSuiteSelect = (suiteId: string) => {
    const updatedSelectedSuiteIds: string[] = filterUpdatedSelectedSuiteIds(
      suiteId,
      selectedSuiteIds
    );

    setSelectedSuiteIds(updatedSelectedSuiteIds);
  };

  const handleSelectAllSuites = () => {
    const allEligibleSuiteIds: string[] = suites
      .filter((suite: ISuite) => suite.isKeyHandOff === false)
      .map((suite: ISuite) => suite.id);
    setSelectedSuiteIds(allEligibleSuiteIds);
  };

  const handleDeselectAllSuites = () => {
    setSelectedSuiteIds([]);
  };

  const handleKeyValueValidationChange = (hasErrors: boolean) => {
    setHasKeyValueValidationErrors(hasErrors);
  };

  const isFormValid = () => {
    const basicValidation: boolean = selectedSuiteIds.length > 0;
    const validKeyValuePairs: boolean = !hasKeyValueValidationErrors;
    return basicValidation && validKeyValuePairs;
  };

  const saveBulkKeyPickups = () => {
    const pairsWithContent: KeyValuePair[] = keyValuePairs.filter(
      (pair: KeyValuePair) => pair.key.trim() && pair.value.trim()
    );

    const pairsWithoutIds: KeyValPairNoID[] = pairsWithContent.map(
      ({ id, ...backendData }: KeyValuePair) => backendData
    );

    const payload: BulkKeyPickupPayloadFromUI = {
      SuiteIds: selectedSuiteIds,
      KeyHandOffInstructions: instructions,
      SuiteKeysAndValues: pairsWithoutIds,
      propertyId: propertyId,
    };

    onSave?.(payload);
  };

  return (
    <div className="key-pickup-form-container">
      <div className="key-pickup-form-content">
        {/* Suite Count Display. */}
        <Callout
          variant="info"
          title={`${eligibleCount} of ${totalSuites} suites available for key pickup configuration`}
          subtitle={
            eligibleCount < totalSuites
              ? 'Some suites are shown as disabled because their key pickup has already been completed.'
              : undefined
          }
          className="key-pickup-suite-count"
        />
        <div className="key-pickup-form-row">
          <div className="key-pickup-form-column">
            {/* Key Pickup Instructions. */}
            <div className="key-pickup-input-group">
              <label className="key-pickup-form-label">
                Key Pickup Instructions
              </label>
              <div className="key-pickup-instructions-container">
                <TextEditor
                  editorState={instructions}
                  onEditorStateChange={setInstructions}
                  readOnly={undefined}
                  placeholder={undefined}
                />
              </div>
            </div>
            {/* Additional Details. */}
            <div className="key-pickup-input-group">
              <label className="key-pickup-form-label">
                Additional Details
              </label>
              <KeyValuePairs
                pairs={keyValuePairs}
                onChange={setKeyValuePairs}
                onValidationChange={handleKeyValueValidationChange}
                keyPlaceholder="Label"
                valuePlaceholder="Description"
                addButtonLabel="+ Add Details"
              />
            </div>
            {/* Suite Selection Table. */}
            <div className="key-pickup-input-group">
              <label className="key-pickup-form-label">
                Choose Suites for Key Pickup Configuration (
                {selectedSuiteIds.length} of {eligibleCount} selected)
              </label>
              <Callout
                variant="warning"
                icon="💡"
                title="You can re-edit key pickup details for pending units as many times as needed."
                subtitle='Only suites with status "Key pickup completed" cannot be modified.'
                className="key-pickup-suite-info"
              />
              <div className="key-pickup-suite-table-section">
                <div className="key-pickup-suite-table-wrapper">
                  <BulkKeyPickupsSuiteTable
                    suites={suites}
                    selectedSuiteIds={selectedSuiteIds}
                    onSuiteSelect={handleSuiteSelect}
                    onSelectAll={handleSelectAllSuites}
                    onDeselectAll={handleDeselectAllSuites}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions. */}
      <div className="key-pickup-form-actions">
        <LoadingButton
          actionText="Configure Key Pickups"
          selectedCount={selectedSuiteIds.length}
          countLabel={selectedSuiteIds.length === 1 ? 'suite' : 'suites'}
          isSubmitting={isSubmitting}
          disabled={!isFormValid()}
          onClick={saveBulkKeyPickups}
        />
      </div>
    </div>
  );
};

export default BulkAddKeyPickups;
